import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type PropsWithChildren, type ReactNode, type Ref, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type WithInteractiveProps = { isInteractive: true; ariaLabel: string };
type WithoutInteractiveProps = { isInteractive: false };

type PreviewSectionProps = PropsWithChildren<
  {
    renderModal: (onClose: () => void) => ReactNode;
    containerRef?: Ref<HTMLDivElement>;
    onOpen?: () => void;
  } & (WithInteractiveProps | WithoutInteractiveProps)
>;

export const PreviewSection = (props: PreviewSectionProps) => {
  const { children, isInteractive, renderModal, containerRef, onOpen } = props;
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsFullscreen(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => setIsFullscreen(false), []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleOpen();
      }
    },
    [handleOpen]
  );

  return (
    <>
      <Box sx={{ borderLeft: 1, borderColor: 'divider', p: 4 }}>
        <Typography variant="h3" sx={{ mb: 1.5 }} data-test="preview-section-title">
          {t('Creative Preview')}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box
            ref={containerRef}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.boxLight',
              borderRadius: 1,
              width: '100%',
              minWidth: 160,
              aspectRatio: '16 / 9',
              overflow: 'hidden',
              ...(isInteractive && { cursor: 'pointer' }),
            }}
            onClick={isInteractive ? handleOpen : undefined}
            {...(isInteractive && {
              role: 'button',
              tabIndex: 0,
              'aria-label': props.ariaLabel,
              onKeyDown: handleKeyDown,
            })}
            data-test="creative-preview-container"
          >
            {children}
          </Box>
        </Box>
      </Box>

      {isFullscreen && renderModal(handleClose)}
    </>
  );
};
